import { useNavigation } from '@react-navigation/native';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { Pressable, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useDialog } from '@/hooks/useDialog';
import { Text } from '@/components/Text';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { styles } from './styles';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { usePasswordFormController } from '@/components/ChangePassword/usePasswordFormController';

type PasswordChangeFormProps = {
  passwordResetToken?: string;
};

function PasswordChangeForm({ passwordResetToken }: PasswordChangeFormProps) {
  const { showDialog, removeDialog } = useDialog();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();

  const localDialogOptions = {
    success: {
      title: 'Passwort geändert',
      content: 'Passwort wurde erfolgreich geändert.',
      cancelable: false,
      confirmAction: {
        label: 'weiter',
        onPress: () => {
          removeDialog();
          navigation.navigate('Home');
        },
      },
    },
    error: {
      title: 'Login fehlgeschlagen!',
      content:
        'Ihr Passwort oder Ihre E-Mail-Adresse sind falsch. Oder Sie haben sich noch nicht bei neolexon registriert!',
      cancelable: false,
      danger: true,
    },
  };

  const { control, newPasswordFilled, handlePasswordSubmit } =
    usePasswordFormController({
      passwordResetToken,
      onSuccess: () => showDialog(localDialogOptions.success),
      onError: () => showDialog(localDialogOptions.error),
      onNetworkError: () => showDialog(PREPARED_DIALOGS.noInternetDialog()),
      onResetSuccess: () =>
        navigation.navigate('PublicConfirmationPage', {
          title: 'Ihr Passwort wurde erfolgreich zurückgesetzt',
          text: 'Sie können dieses Fenster schließen und sich erneut in der Neolexon–App anmelden.',
        }),
      onResetError: () =>
        showDialog({
          title: 'Fehler beim Zurücksetzen',
          content:
            'Leider konnte Ihr Passwort nicht zurückgesetzt werden. Versuchen Sie es bitte erneut.',
          danger: true,
          cancelable: false,
        }),
    });

  return (
    <View style={styles.loginContainer}>
      <KeyboardAwareScrollView
        contentContainerStyle={[styles.wrapper]}
        enableOnAndroid={true}
        extraHeight={useResponsiveHeight(30)}
      >
        <View style={styles.formContent}>
          {passwordResetToken ? null : (
            <SharedFormField
              control={control}
              name="currentPassword"
              label="Aktuelles Passwort"
              rules={{ required: 'Pflichtfeld' }}
              secureTextEntry
              usePasswordInput
            />
          )}

          <View style={styles.fieldSpacing}>
            <SharedFormField
              control={control}
              name="newPassword"
              label="Neues Passwort"
              rules={{
                required: 'Pflichtfeld',
                minLength: {
                  value: 8,
                  message:
                    'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                },
                validate: (value: string) => passwordValidate(value),
              }}
              secureTextEntry
              usePasswordInput
            />
          </View>
          <View style={styles.fieldSpacing}>
            <SharedFormField
              control={control}
              name="verifyPassword"
              label="Neues Passwort wiederholen"
              rules={{
                required: 'Pflichtfeld',
                validate: (value: string) =>
                  confirmPasswordValidate(value, newPasswordFilled),
              }}
              secureTextEntry
              usePasswordInput
            />
          </View>
          <Pressable
            style={styles.passwordSubmit}
            onPress={handlePasswordSubmit}
          >
            <Text style={styles.submitText}>Passwort ändern</Text>
          </Pressable>
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}

export default PasswordChangeForm;
